import React from 'react';
import { useTranslation } from 'react-i18next';
import type { MfaMethod } from '../../types';
import { BRAND, BRAND_LIGHT } from '../../lib/theme';

export interface MfaMethodPickerProps {
  available: MfaMethod[];
  selected: MfaMethod;
  onSelect: (method: MfaMethod) => void;
}

const METHOD_ICONS: Record<MfaMethod, React.ReactNode> = {
  email: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/>
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
    </svg>
  ),
  sms: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  totp: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
      <path d="M12 18h.01"/>
    </svg>
  ),
};

export function MfaMethodPicker({ available, selected, onSelect }: MfaMethodPickerProps) {
  const { t } = useTranslation();

  return (
    <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
      <legend
        style={{ fontSize: 13, color: '#667085', marginBottom: 10, fontWeight: 500 }}
        id="mfa-method-picker-legend"
      >
        {t('mfa.challenge.method_picker_label')}
      </legend>
      <div
        role="radiogroup"
        aria-labelledby="mfa-method-picker-legend"
        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        {available.map(method => {
          const isSelected = method === selected;
          return (
            <label
              key={method}
              htmlFor={`mfa-method-${method}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 14px',
                borderRadius: 10,
                border: `2px solid ${isSelected ? BRAND : '#e4e7ec'}`,
                background: isSelected ? BRAND_LIGHT : '#fff',
                cursor: 'pointer',
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              <input
                id={`mfa-method-${method}`}
                type="radio"
                name="mfa-method"
                value={method}
                checked={isSelected}
                onChange={() => onSelect(method)}
                style={{ display: 'none' }}
              />
              <span style={{ color: isSelected ? BRAND : '#667085', flexShrink: 0 }}>
                {METHOD_ICONS[method]}
              </span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#101828' }}>
                {t(`mfa.setup.method.${method}.title`)}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
